import type { Strategies } from "@/../config/swr.config";
import type { SupProtocolRewardToken } from "@/hooks/rewards/useProtocolRewards";
import { useCallback, useMemo, useState } from "react";

import useComposedRewards, { type SupComposedRewardStatus } from "@/hooks/rewards/useComposedRewards";

export type groupByOptions = "all" | SupComposedRewardStatus;

export interface SupProtocolRewardGroupedByToken {
  token: SupProtocolRewardToken;
  amount: bigint;
  amount_usd: number;
}

interface useProtocolRewardsTokensProps {
  strategy?: Strategies;
  defaultGroupBy?: groupByOptions;
  filterByChain?: number;
}

export default function useProtocolRewardsTokens({ strategy = "on-mount", defaultGroupBy = "all", filterByChain }: useProtocolRewardsTokensProps = {}) {
  const [groupBy, setGroupBy] = useState<groupByOptions>(defaultGroupBy);
  const { data: rewardsTokens, isLoading: isLoadingRewardsTokens, isError: isErrorRewardsTokens } = useComposedRewards(strategy);
  const handleGroupByChange = useCallback((value: groupByOptions) => {
    setGroupBy(value);
  }, []);
  return useMemo(
    () => ({
      isLoading: isLoadingRewardsTokens,
      isError: isErrorRewardsTokens,
      data:
        rewardsTokens
          ?.filter(item => (groupBy === "all" || item.type === groupBy) && (!filterByChain || item.chain_id === filterByChain))
          .reduce<Record<string, SupProtocolRewardGroupedByToken>>((acc, item) => {
            if (!item.token) {
              return acc;
            }
            const tokenId = item.token?.id;
            return {
              ...acc,
              [tokenId]: {
                token: item.token,
                amount: (acc[tokenId]?.amount ?? 0n) - BigInt(`${item.amount}`) * -1n,
                amount_usd: (acc[tokenId]?.amount_usd ?? 0) + item.amount_usd,
              },
            };
          }, {}) ?? {},
      handleGroupByChange,
      groupBy,
    }),
    [filterByChain, groupBy, handleGroupByChange, isErrorRewardsTokens, isLoadingRewardsTokens, rewardsTokens]
  );
}
